import React, { useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { GET_NEWS_DETAILS_BY_ID, GET_NEWS_EDIT_REALESTATES } from "src/api/news";
import { GetNewsDetailsById, GetNewsDetailsByIdVariables } from "src/api/generated/GetNewsDetailsById";
import { GetNewsEditRealestates, GetNewsEditRealestatesVariables } from "src/api/generated/GetNewsEditRealestates";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { Route, selectRoute } from "src/config/routes";
import { withRouter, useParams } from "react-router";
import { NewsEditForm } from "./form/NewsEditForm";
import { getInitialNewsRealestateSelection, INewsRealestateSelection } from "./form/NewsEditForm.utils";

const EMPTY_REALESTATE_SELECTION: INewsRealestateSelection = { realestates: [], inaccessibleRealestateIds: [] };

interface Params {
    realestateid: string;
    newsid: string;
    newspreviewnewsid: string;
}

const NewsEditBase = () => {
    const { realestateid, newsid, newspreviewnewsid } = useParams<Params>();

    const { t } = useTranslation();
    const { navStore, authStore, uiStore } = useContext(RootStoreContext);

    useEffect(() => {
        if (newspreviewnewsid) {
            navStore.setBackbutton(
                selectRoute(Route.realestateNewsPreview, authStore.user?.role, {
                    realestateid: realestateid,
                    newsid: newsid
                }),
                t("screens.realestate.news_preview.title").toString()
            );
        } else {
            navStore.setBackbutton(
                selectRoute(Route.dashboard, authStore.user?.role, { realestateid: realestateid }),
                t("screens.realestate.dashboard.title").toString()
            );
        }
    }, [authStore.user?.role, navStore, realestateid, t, newsid, newspreviewnewsid]);

    const { data, loading, error, refetch } = useQuery<GetNewsDetailsById, GetNewsDetailsByIdVariables>(
        GET_NEWS_DETAILS_BY_ID,
        {
            variables: {
                newsid: newsid
            }
        }
    );

    useEffect(() => {
        refetch();
    }, [uiStore.dbNewsFiles, refetch]);

    const {
        data: realestatesData,
        loading: isLoadingRealestates,
        error: realestatesError
    } = useQuery<GetNewsEditRealestates, GetNewsEditRealestatesVariables>(GET_NEWS_EDIT_REALESTATES, {
        variables: { newsid, realestateid },
        skip: !navStore.hasFeatureFlagNews,
        fetchPolicy: "no-cache"
    });

    const initialRealestateSelection = useMemo(
        () => (realestatesData ? getInitialNewsRealestateSelection(realestatesData) : EMPTY_REALESTATE_SELECTION),
        [realestatesData]
    );

    if (loading || isLoadingRealestates) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (realestatesError) {
        return <Screens.Error message={realestatesError.message} networkError={realestatesError.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    const newsDetails = data.newsitems.length > 0 ? data.newsitems[0] : undefined;

    return (
        <NewsEditForm
            newsDetails={newsDetails}
            newsid={newsid}
            realestateid={realestateid}
            initialRealestateSelection={initialRealestateSelection}
            refetchData={refetch}
        />
    );
};

export const NewsEdit = withRouter(Sentry.withProfiler(observer(NewsEditBase)));
